<template>
  <div
    class="w-full h-auto min-h-screen mt-3 justify-between bg-gray-50 dark:bg-background space-y-9 rounded-t-[20px]"
  >
    <div class="mx-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-20">
        <Loader />
      </div>

      <!-- Content when loaded -->
      <div v-else>
        <div class="hr-container mb-2">
          <div class="flex items-start justify-between">
            <div class="space-y-3 flex-1">
              <p class="font-semibold text-bodyText text-[20px]">
                {{ goal?.topic || "Learning Goal" }}
              </p>
            </div>
            <LearningGoalActionsBar
              v-if="goal"
              :goal="goal"
              :projectId="projectId"
              @book-mentor="onBookMentor"
              @edit-goal="onEditGoal"
            />
          </div>
          <!-- Full-width transparent description below row -->
          <div
            class="mt-2 w-full min-w-0 bg-transparent border border-border rounded-[12px] p-3"
          >
            <div class="flex items-start gap-2">
              <p class="text-bodyText text-[14px] flex-1 break-words">
                {{
                  descriptionExpanded
                    ? goal?.description || defaultDescription
                    : truncateDescription(goal?.description) ||
                      defaultDescription
                }}
              </p>
              <button
                type="button"
                class="h-6 w-6 flex-shrink-0 flex items-center justify-center hover:bg-muted transition-colors"
                @click="toggleDescription"
                :aria-expanded="descriptionExpanded.toString()"
                aria-label="Toggle description"
              >
                <svg
                  :class="{ 'rotate-180': descriptionExpanded }"
                  class="transition-transform"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="#6E6E6F"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Certificates Section -->
        <div class="space-y-8">
          <!-- Course Completion Badge -->
          <div
            v-if="certificatesByType.course_completion"
            class="bg-card rounded-[12px] border border-border p-8"
          >
            <div class="flex items-start gap-6">
              <div class="flex-shrink-0">
                <img
                  :src="
                    require(`@/assets/projects/${certificatesByType.course_completion.badge}.svg`)
                  "
                  :alt="certificatesByType.course_completion.achievement_name"
                  class="w-24 h-24"
                />
              </div>
              <div class="flex-grow">
                <h3 class="text-[18px] font-semibold text-bodyText mb-2">
                  {{
                    certificatesByType.course_completion.achievement_name ||
                    "Congratulations!"
                  }}
                </h3>
                <p class="text-[14px] text-gray-600 mb-2">
                  {{
                    certificatesByType.course_completion.description ||
                    "You have earned a course completion badge🎉. A proof of your consistency and hard work. Keep it up!"
                  }}
                </p>
                <div class="flex gap-3">
                  <button
                    @click="startAIAssessment"
                    :disabled="aiAssessmentCompleted"
                    class="px-6 py-2 text-[14px] font-medium text-white bg-black border border-white rounded-[8px] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Start AI Assessment
                  </button>
                  <button
                    class="px-6 py-2 text-[14px] font-medium bg-card text-foreground border border-border rounded-[8px] hover:bg-muted transition-colors"
                  >
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- AI Verified Certificate -->
          <div
            v-if="certificatesByType.ai_verified"
            class="bg-card rounded-[12px] border border-border p-8"
          >
            <div class="flex items-start gap-6">
              <div class="flex-shrink-0">
                <img
                  :src="
                    require(`@/assets/projects/${certificatesByType.ai_verified.badge}.svg`)
                  "
                  :alt="certificatesByType.ai_verified.achievement_name"
                  class="w-24 h-24"
                />
              </div>
              <div class="flex-grow">
                <h3 class="text-[18px] font-semibold text-bodyText mb-2">
                  {{
                    certificatesByType.ai_verified.achievement_name ||
                    "AI verified"
                  }}
                </h3>
                <p class="text-[14px] text-gray-600 mb-2">
                  {{
                    certificatesByType.ai_verified.description ||
                    "Validated by AI! Your skill is hereby certified. Keep it up!"
                  }}
                </p>
                <div class="flex gap-3">
                  <button
                    @click="startHumanAssessment"
                    :disabled="humanAssessmentCompleted"
                    class="px-6 py-2 text-[14px] font-medium text-white bg-black border border-white rounded-[8px] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Start Human Assessment
                  </button>
                  <button
                    class="px-6 py-2 text-[14px] font-medium text-black bg-white border border-black rounded-[8px] hover:bg-gray-50 transition-colors"
                  >
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Certified Professional Certificate -->
          <div
            v-if="certificatesByType.certified_professional"
            class="bg-card rounded-[12px] border border-border p-8"
          >
            <div class="flex items-start gap-6">
              <div class="flex-shrink-0">
                <img
                  :src="
                    require(`@/assets/projects/${certificatesByType.certified_professional.badge}.svg`)
                  "
                  :alt="
                    certificatesByType.certified_professional.achievement_name
                  "
                  class="w-24 h-24"
                />
              </div>
              <div class="flex-grow">
                <h3 class="text-[18px] font-semibold text-bodyText mb-2">
                  {{
                    certificatesByType.certified_professional
                      .achievement_name || "Certified Professional!"
                  }}
                </h3>
                <p class="text-[14px] text-gray-600 mb-2">
                  {{
                    certificatesByType.certified_professional.description ||
                    "Your performance was verified by experts having passed the interview stage."
                  }}
                </p>
                <div class="flex gap-3">
                  <button
                    @click="downloadCertificate"
                    class="px-6 py-2 text-[14px] font-medium text-black bg-white border border-black rounded-[8px] hover:bg-gray-50 transition-colors"
                  >
                    Download certificate
                  </button>
                  <button
                    class="px-6 py-2 text-[14px] font-medium text-white bg-black border border-white rounded-[8px] hover:opacity-90 transition-opacity"
                  >
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="!loading && certificates.length === 0"
            class="bg-white rounded-[12px] border border-[#E5E7EB] p-8 text-center"
          >
            <p class="text-[14px] text-gray-600">
              No certificates earned yet. Complete your learning goals and
              assessments to earn certificates!
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Celebration Modal -->
    <teleport to="body">
      <div
        v-if="showCelebration"
        class="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      >
        <div class="bg-card w-full max-w-md rounded-[24px] p-8 text-center border border-border shadow-2xl relative overflow-hidden mx-4">
        <button
          @click="closeCelebration"
          class="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        
        <div class="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-black/5 dark:bg-white/10">
          <span class="text-4xl">{{ celebrationData.icon }}</span>
        </div>
        
        <h2 class="text-2xl font-bold mb-2 text-foreground">{{ celebrationData.title }}</h2>
        <p class="text-muted-foreground mb-8 text-[15px] leading-relaxed">
          {{ celebrationData.message }}
        </p>
        
        <div class="space-y-3">
          <button
            @click="handlePrimaryAction"
            class="w-full py-3 px-4 bg-black dark:bg-white text-white dark:text-black rounded-[12px] font-medium hover:opacity-90 transition-opacity"
          >
            {{ celebrationData.primaryText }}
          </button>
          
          <button
            @click="handleSecondaryAction"
            class="w-full py-3 px-4 bg-transparent border border-border text-foreground rounded-[12px] font-medium hover:bg-muted transition-colors"
          >
            {{ celebrationData.secondaryText }}
          </button>
        </div>
      </div>
    </div>
    </teleport>
  </div>
</template>

<script>
import apiClient from "@/axios";
import LearningGoalActionsBar from "./LearningGoalActionsBar.vue";
import Loader from "../ui/Loader.vue";
import confetti from "canvas-confetti";

export default {
  name: "CertificatesComponent",
  components: {
    LearningGoalActionsBar,
    Loader,
  },
  data() {
    return {
      goal: null,
      certificates: [],
      loading: true,
      showError: false,
      errorMessage: "",
      descriptionExpanded: false,
      defaultDescription: "View your certificates and achievements.",
      showCelebration: false,
    };
  },
  computed: {
    projectId() {
      return this.$route.params.projectId;
    },
    aiAssessmentCompleted() {
      return (this.goal?.ai_assessment_progress?.progress || 0) >= 100;
    },
    humanAssessmentCompleted() {
      return (this.goal?.human_assessment_progress?.progress || 0) >= 100;
    },
    certificatesByType() {
      const grouped = {
        course_completion: null,
        ai_verified: null,
        certified_professional: null,
      };

      this.certificates.forEach((cert) => {
        if (cert.certificate_type === "course_completion") {
          grouped.course_completion = cert;
        } else if (cert.certificate_type === "ai_verified") {
          grouped.ai_verified = cert;
        } else if (cert.certificate_type === "certified_professional") {
          grouped.certified_professional = cert;
        }
      });

      return grouped;
    },
    celebrationData() {
      const level = this.goal?.metadata?.target_skill_level?.toLowerCase() || 'beginner';
      const topic = this.goal?.metadata?.topic || 'this topic';
      
      if (level === 'beginner') {
        return {
          icon: '🎉',
          title: 'Beginner Skill Path Complete!',
          message: `You've mastered the basics of ${topic}. Ready to level up?`,
          primaryText: 'Start Intermediate Level',
          secondaryText: 'View Certificates',
          nextLevel: 'intermediate',
          isMaxLevel: false
        };
      } else if (level === 'intermediate') {
        return {
          icon: '🔥',
          title: 'Intermediate Skill Path Complete!',
          message: `You're making great progress in ${topic}. Time for the ultimate challenge!`,
          primaryText: 'Start Advanced Level',
          secondaryText: 'View Certificates',
          nextLevel: 'advanced',
          isMaxLevel: false
        };
      } else {
        return {
          icon: '👑',
          title: 'Mastery Achieved!',
          message: `You have conquered the highest level of ${topic}. What's next on your journey?`,
          primaryText: 'Explore Related Skills',
          secondaryText: 'Apply for Apprenticeships',
          nextLevel: null,
          isMaxLevel: true
        };
      }
    },
  },
  methods: {
    async fetchGoal() {
      try {
        this.loading = true;
        const response = await apiClient.get(
          `/web/learning-goals/${this.projectId}`,
        );
        this.goal = response.data || null;

        if (this.$route.query.celebrate === 'true') {
          setTimeout(() => {
            this.triggerCelebration();
          }, 300);
        }
      } catch (error) {
        console.error("Error fetching learning goal:", error);
        this.handleError("Failed to load learning goal. Please try again.");
      } finally {
        this.loading = false;
      }
    },

    async fetchCertificates() {
      try {
        const response = await apiClient.get(
          `/web/learning-goals/${this.projectId}/certificates`,
        );
        this.certificates = response.data || [];
      } catch (error) {
        console.error("Error fetching certificates:", error);
      }
    },

    truncateDescription(description) {
      if (!description) return "";
      const maxLength = 100;
      if (description.length <= maxLength) return description;
      return description.substring(0, maxLength).trim() + "...";
    },

    toggleDescription() {
      this.descriptionExpanded = !this.descriptionExpanded;
    },

    onBookMentor(response) {
      if (response && response.message) {
        this.$toast.success("Session Booked", response.message);
      }
    },

    onEditGoal() {
      this.$router.push(`/learning-goals/edit/${this.projectId}`);
    },

    navigateToGoalDetails() {
      this.$router.push({
        name: "LearningGoalDetails",
        params: { projectId: this.projectId },
      });
    },

    startAIAssessment() {
      this.$router.push({
        name: "AIAssessment",
        params: { projectId: this.projectId },
      });
    },

    startHumanAssessment() {
      // TODO: Navigate to Human Assessment page when implemented
      console.log("Starting Human Assessment...");
    },

    downloadCertificate() {
      // TODO: Implement certificate download
      console.log("Downloading certificate...");
    },

    handleError(message) {
      this.showError = true;
      this.errorMessage = message;
      setTimeout(() => {
        this.showError = false;
        this.errorMessage = "";
      }, 5000);
    },

    triggerCelebration() {
      this.showCelebration = true;
      
      const duration = 3 * 1000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 5,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          zIndex: 100000
        });
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          zIndex: 100000
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();
    },

    closeCelebration() {
      this.showCelebration = false;
      const query = { ...this.$route.query };
      delete query.celebrate;
      this.$router.replace({ query });
    },

    handlePrimaryAction() {
      this.showCelebration = false;
      const topic = this.goal?.metadata?.topic || '';
      if (!this.celebrationData.isMaxLevel) {
        this.$router.push({
          path: '/learning-goals/create',
          query: {
            topic: topic,
            level: this.celebrationData.nextLevel,
            skip_to_summary: 'true'
          }
        });
      } else {
        this.$router.push({
          path: '/learning-goals/create'
        });
      }
    },

    handleSecondaryAction() {
      if (!this.celebrationData.isMaxLevel) {
        this.closeCelebration();
      } else {
        this.showCelebration = false;
        const topic = this.goal?.metadata?.topic || '';
        this.$router.push({
          path: '/jobs',
          query: { search: topic }
        });
      }
    }
  },

  created() {
    this.fetchGoal();
    this.fetchCertificates();
  },
};
</script>

<style scoped></style>
